import React, {FC, memo} from "react";
import {IM10BannerBundleBlock, IM10BannerCardBlock, IM24CompositionCard} from "@montana/models/generated/cms.interface";

import contrast from 'contrast';
import cx from 'clsx';
import {M10BannerCardComponent} from "@montana/shared/m10-banner-card/m10-banner-card.component";
import Carousel from "@montana/shared/carousel";
import {CompositionCard} from "@montana/shared/composition-card/composition-card.component";
import {Link} from "@montana/shared/link";

export type LineTruncateDescription = 2 | 3;

export interface IM10BannerBundleBlockProps extends IM10BannerBundleBlock {
    truncateDescription?: LineTruncateDescription;
}

export const M10BannerBundleBlock: FC<IM10BannerBundleBlockProps> = memo((props: IM10BannerBundleBlockProps) => {
    const {title, cards, mediaDisplayRatio, backgroundColorHex, carouselMode, truncateDescription, link} = props;

    const bannerCards = ((cards as (IM10BannerCardBlock | IM24CompositionCard)[]) || [])
        .map((card, index) => {
            const key = `banner-bunde__card__${index}`;
            switch (card.kind) {
                case 'M10BannerCardBlock':
                    return (
                        <M10BannerCardComponent
                            key={key}
                            mediaDisplayRatio={mediaDisplayRatio}
                            truncateDescription={truncateDescription}
                            {...card}
                        />
                    );
                case 'M24CompositionCard':
                    return (<div className="banner-bundle-card"><CompositionCard key={key} {...card} /></div>);
                default:
                    return null;
            }
        })
        .filter((card): card is JSX.Element => card !== null);

    if (!bannerCards || bannerCards.length === 0) {
        return null;
    }

    return (
        <section
            className={cx(`banner-bundle banner-bundle--${mediaDisplayRatio}`, {
                'banner-bundle--dark': backgroundColorHex && contrast(backgroundColorHex) === 'dark',
                'banner-bundle--no-background': !backgroundColorHex,
                'banner-bundle--carousel': carouselMode
            })}
            style={{backgroundColor: backgroundColorHex}}
        >
            {carouselMode ? (
                <Carousel
                    header={title && (<div className="banner-bundle__carousel-header rich-text"><h2
                        className="banner-bundle__title rte-display-4"
                        dangerouslySetInnerHTML={{__html: title}}></h2>{link?.url && <Link
                        to={link.url} className="banner-bundle__carousel-header-link" withStroke>{link.name}</Link>}
                    </div>)}
                    lowHeaderSpacing
                    insideBrokenContainer

                >
                    {bannerCards}
                </Carousel>
            ) : (
                <div className="banner-bundle__content">
                    {title && (
                        <span className="rich-text">
                            <h2 className="banner-bundle__title rte-display-4" dangerouslySetInnerHTML={{__html: title}}></h2>
                        </span>
                    )}
                    <div className="banner-bundle__cards">
                        {bannerCards}
                    </div>
                </div>
            )}
        </section>
    );
});

M10BannerBundleBlock.displayName = 'M10BannerBundleBlock';
